import { z } from 'zod';

/**
 * GLOBAL email-poller configuration (operator-tuned, v1.13.0). The email poller is a
 * single platform-level loop, so this config is ONE global row — NOT per-workspace. It
 * is an OPERATOR resource: the API (`GET`/`PUT /api/admin/poller-config`) is gated to the
 * platform-operator allow-list, never reachable per workspace.
 *
 * `pollerConfigUpdate` is the writable shape (the PUT body) — all fields present, so a
 * PUT is a full replace of the operator-tunable knobs. `pollerConfig` is what the API
 * returns: the same knobs plus server-owned `updatedAt`.
 *
 * A later task makes the poller READ this config; this contract only defines its shape
 * and validation (min/max, HH:MM format, IANA timezone).
 */

/** Zero-padded 24h time-of-day, `HH:MM` (00:00–23:59). */
export const POLLER_HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Minimum poll cadence (minutes). At least once per minute. */
export const POLLER_MIN_FREQUENCY_MINUTES = 1;
/** Maximum poll cadence (minutes) — 1440 = once per day. */
export const POLLER_MAX_FREQUENCY_MINUTES = 1440;

/**
 * True when `tz` is a time zone the host's `Intl` accepts (a practical IANA check —
 * `Intl.DateTimeFormat` throws `RangeError` for an unknown zone). Node ≥22 ships the
 * full tz database, so this admits every IANA name (e.g. "Europe/London", "UTC").
 */
export function isValidIanaTimeZone(tz: unknown): boolean {
  if (typeof tz !== 'string' || tz.length === 0) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** The writable operator knobs — the `PUT /api/admin/poller-config` body (full replace). */
export const pollerConfigUpdate = z.object({
  /** How often the poller loop runs, in minutes. Min 1, max 1440 (once/day). */
  checkFrequencyMinutes: z
    .number()
    .int('checkFrequencyMinutes must be an integer')
    .min(POLLER_MIN_FREQUENCY_MINUTES, 'checkFrequencyMinutes must be at least 1')
    .max(POLLER_MAX_FREQUENCY_MINUTES, 'checkFrequencyMinutes must be at most 1440'),
  /** When false the poller runs 24/7 and the active-hours fields are ignored. */
  activeHoursEnabled: z.boolean(),
  /** `HH:MM` window bounds, interpreted in `activeHoursTimezone` (only meaningful when enabled). */
  activeHoursStart: z.string().regex(POLLER_HHMM, 'activeHoursStart must be a 24h time of day, HH:MM'),
  activeHoursEnd: z.string().regex(POLLER_HHMM, 'activeHoursEnd must be a 24h time of day, HH:MM'),
  /** IANA time zone the active-hours window is read in, e.g. "Europe/London". */
  activeHoursTimezone: z
    .string()
    .refine(isValidIanaTimeZone, 'activeHoursTimezone must be a valid IANA time zone, e.g. "Europe/London"'),
  /** When true the poller auto-skips obvious non-actionable mail (the filter is a later task). */
  filterNonActionable: z.boolean(),
});
export type PollerConfigUpdate = z.infer<typeof pollerConfigUpdate>;

/** What the API returns — the operator knobs plus the server-owned last-updated instant. */
export const pollerConfig = pollerConfigUpdate.extend({
  /** ISO-8601 UTC timestamp of the last update; `null` when serving unsaved defaults. */
  updatedAt: z.string().datetime().nullable(),
});
export type PollerConfig = z.infer<typeof pollerConfig>;

/** The documented defaults — the poller's behaviour when no operator has tuned it. */
export const POLLER_CONFIG_DEFAULTS: PollerConfigUpdate = {
  checkFrequencyMinutes: 5,
  activeHoursEnabled: false,
  activeHoursStart: '00:00',
  activeHoursEnd: '23:59',
  activeHoursTimezone: 'UTC',
  filterNonActionable: true,
};
